import { Ban, Compass, RefreshCw } from 'lucide-react'
import { Link, useRouteError } from 'react-router-dom'

import { Button } from '@/components/ui/Button'

function Shell({
  icon,
  code,
  title,
  description,
  actions,
}: {
  icon: React.ReactNode
  code: string
  title: string
  description: string
  actions: React.ReactNode
}) {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center px-6 text-center">
      <div className="bg-surface-sunken border-border mb-5 rounded-xl border p-3.5">{icon}</div>
      <p className="text-subtle-foreground font-mono text-[11px] tracking-widest uppercase">
        {code}
      </p>
      <h1 className="mt-2 text-xl font-semibold tracking-tight">{title}</h1>
      <p className="text-muted-foreground mt-2 max-w-sm text-[13px] leading-relaxed">
        {description}
      </p>
      <div className="mt-6 flex items-center gap-2">{actions}</div>
    </div>
  )
}

export function NotFoundPage() {
  return (
    <Shell
      icon={<Compass className="text-subtle-foreground size-5" strokeWidth={1.5} />}
      code="404"
      title="Page not found"
      description="That page does not exist, or it moved somewhere else in the console."
      actions={
        <Link to="/">
          <Button variant="primary" size="sm">
            Back to the dashboard
          </Button>
        </Link>
      }
    />
  )
}

export function ForbiddenPage({ permission }: { permission?: string }) {
  return (
    <Shell
      icon={<Ban className="text-danger size-5" strokeWidth={1.5} />}
      code="403"
      title="You do not have access"
      description={
        permission
          ? `This page needs the "${permission}" permission. Ask an administrator to grant it to one of your Discord roles.`
          : 'Your Discord roles do not grant access to this page.'
      }
      actions={
        <Link to="/">
          <Button variant="secondary" size="sm">
            Back to the dashboard
          </Button>
        </Link>
      }
    />
  )
}

/** Router-level boundary, so a render crash does not blank the whole console. */
export function RouteErrorPage() {
  const error = useRouteError()
  const message =
    error instanceof Error ? error.message : 'An unexpected error occurred while rendering.'

  return (
    <Shell
      icon={<RefreshCw className="text-warning size-5" strokeWidth={1.5} />}
      code="Error"
      title="Something broke"
      description={message}
      actions={
        <>
          <Button variant="primary" size="sm" onClick={() => window.location.reload()}>
            Reload
          </Button>
          <Link to="/">
            <Button variant="ghost" size="sm">
              Dashboard
            </Button>
          </Link>
        </>
      }
    />
  )
}
